import { type FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppBar } from "../../components/AppShell";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import { initials } from "../../lib/helpers";
import type { Role, StudentRole, User } from "../../lib/types";

const ROLES: { value: StudentRole; cls: string; icon: string; label: string }[] = [
  { value: "medic", cls: "role-medic", icon: "fa-user-doctor", label: "Medic" },
  { value: "rezident", cls: "role-rezident", icon: "fa-stethoscope", label: "Rezident" },
  { value: "asistent", cls: "role-asistent", icon: "fa-hand-holding-medical", label: "Asistent" },
];

const PILL_STYLES = `
.role-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: .78rem; font-weight: 700; }
.role-medic { background: var(--c-primary-light); color: var(--c-primary-dark); }
.role-rezident { background: #fdf1de; color: #8a5a04; }
.role-asistent { background: #ede4fa; color: #4a2c91; }
.role-superadmin { background: var(--c-accent-soft); color: #a8092b; }
.role-editor { background: var(--c-primary-light); color: var(--c-primary-dark); }
.role-examinator { background: #fdf1de; color: #8a5a04; }
.role-support { background: #e6f6ee; color: #0b6b46; }
`;

export function AdminStudents() {
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<StudentRole | null>(null);
  const [editing, setEditing] = useState<User | null | "new">(null);

  const { data: all = [] } = useQuery({
    queryKey: ["admin", "students"],
    queryFn: () => request<User[]>("/users/students"),
  });

  const counts = useMemo(() => ({
    medic: all.filter((u) => u.role === "medic").length,
    rezident: all.filter((u) => u.role === "rezident").length,
    asistent: all.filter((u) => u.role === "asistent").length,
  }), [all]);
  const list = filter ? all.filter((u) => u.role === filter) : all;

  const createMut = useMutation({
    mutationFn: (body: object) => request<User>("/admin/users", { method: "POST", body }),
    onSuccess: () => {
      toast("Student adăugat", "success");
      qc.invalidateQueries({ queryKey: ["admin", "students"] });
      setEditing(null);
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) =>
      request(`/admin/users/${id}`, { method: "PATCH", body }),
    onSuccess: () => {
      toast("Student actualizat", "success");
      qc.invalidateQueries({ queryKey: ["admin", "students"] });
      setEditing(null);
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => request(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("Student șters", "success");
      qc.invalidateQueries({ queryKey: ["admin", "students"] });
      setEditing(null);
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  return (
    <>
      <style>{PILL_STYLES}</style>
      <AppBar crumbs={[{ label: "Tablou de bord", to: "/admin/dashboard" }, { label: "Studenți" }]} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Studenți</h1>
            <p>Gestionează contul, accesul și progresul fiecărui cursant.</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <i className="fa-solid fa-plus"></i> Adaugă student
            </button>
          </div>
        </div>

        <div className="kpi-grid">
          <Kpi label="Total studenți" value={all.length} icon="fa-users" />
          <Kpi label="Medici" value={counts.medic} icon="fa-user-doctor" iconBg="var(--c-primary-light)" iconColor="var(--c-primary-dark)" />
          <Kpi label="Rezidenți" value={counts.rezident} icon="fa-stethoscope" iconBg="#fdf1de" iconColor="#8a5a04" />
          <Kpi label="Asistenți" value={counts.asistent} icon="fa-hand-holding-medical" iconBg="#ede4fa" iconColor="#4a2c91" />
        </div>

        <div className="card">
          <div className="tabs" style={{ marginBottom: 8 }}>
            <div className={"tab" + (filter === null ? " active" : "")} onClick={() => setFilter(null)}>Toți ({all.length})</div>
            {ROLES.map((r) => (
              <div key={r.value} className={"tab" + (filter === r.value ? " active" : "")} onClick={() => setFilter(r.value)}>
                <i className={`fa-solid ${r.icon}`}></i> {r.label}i ({counts[r.value]})
              </div>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Student</th><th>Rol</th><th>Specialitate</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={5} className="text-muted">Niciun student.</td></tr>
                ) : list.map((u) => (
                  <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setEditing(u)}>
                    <td>
                      <div className="user-cell">
                        <div className="avatar avatar-sm">{initials(u.full_name)}</div>
                        <div><strong>{u.full_name}</strong><br /><small className="muted">{u.email}</small></div>
                      </div>
                    </td>
                    <td><RolePill role={u.role as Role} /></td>
                    <td>{u.specialty || "—"}</td>
                    <td>{u.is_active ? <span className="pill success">Activ</span> : <span className="pill danger">Suspendat</span>}</td>
                    <td className="text-right">
                      <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={(e) => { e.stopPropagation(); setEditing(u); }}>
                        <i className="fa-solid fa-pen"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <UserModal
          open={editing !== null}
          user={editing && editing !== "new" ? editing : null}
          mode="student"
          onClose={() => setEditing(null)}
          onSubmit={(payload) => {
            if (editing === "new") createMut.mutate(payload);
            else if (editing) updateMut.mutate({ id: editing.id, body: payload });
          }}
          onDelete={editing && editing !== "new" ? () => {
            if (confirm("Sigur ștergi acest student?")) deleteMut.mutate(editing.id);
          } : undefined}
        />
      </div>
    </>
  );
}

function RolePill({ role }: { role: Role }) {
  const map: Record<string, { cls: string; icon: string; label: string }> = {
    medic: { cls: "role-medic", icon: "fa-user-doctor", label: "Medic" },
    rezident: { cls: "role-rezident", icon: "fa-stethoscope", label: "Rezident" },
    asistent: { cls: "role-asistent", icon: "fa-hand-holding-medical", label: "Asistent" },
    super_admin: { cls: "role-superadmin", icon: "fa-crown", label: "Super admin" },
    editor: { cls: "role-editor", icon: "fa-pen-to-square", label: "Editor curs" },
    examinator: { cls: "role-examinator", icon: "fa-clipboard-check", label: "Examinator" },
    support: { cls: "role-support", icon: "fa-headset", label: "Suport" },
  };
  const m = map[role];
  return <span className={"role-pill " + m.cls}><i className={`fa-solid ${m.icon}`}></i> {m.label}</span>;
}

function Kpi({ label, value, icon, iconBg, iconColor }: { label: string; value: string | number; icon: string; iconBg?: string; iconColor?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-icon" style={iconBg ? { background: iconBg, color: iconColor } : undefined}><i className={`fa-solid ${icon}`}></i></div>
    </div>
  );
}

export function UserModal({ open, user, mode, onClose, onSubmit, onDelete }: {
  open: boolean;
  user: User | null;
  mode: "student" | "admin";
  onClose: () => void;
  onSubmit: (payload: any) => void;
  onDelete?: () => void;
}) {
  const isEdit = !!user;
  const initial = user || { email: "", full_name: "", role: mode === "student" ? "rezident" : "editor", specialty: "", is_active: true } as any;

  const [fullName, setFullName] = useState(initial.full_name);
  const [email, setEmail] = useState(initial.email);
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>(initial.role);
  const [isActive, setIsActive] = useState<boolean>(initial.is_active);
  const [specialty, setSpecialty] = useState(initial.specialty || "");

  // Reset state when modal opens
  const userId = user?.id;
  useMemo(() => {
    if (open) {
      setFullName(initial.full_name);
      setEmail(initial.email);
      setPassword("");
      setRole(initial.role as Role);
      setIsActive(initial.is_active);
      setSpecialty(initial.specialty || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const payload: any = { full_name: fullName, role, specialty: specialty || null, is_active: isActive };
    if (isEdit) {
      if (password) payload.password = password;
    } else {
      payload.email = email;
      payload.password = password;
    }
    onSubmit(payload);
  }

  const roleOptions: { value: Role; label: string }[] = mode === "student"
    ? [{ value: "medic", label: "Medic" }, { value: "rezident", label: "Rezident" }, { value: "asistent", label: "Asistent" }]
    : [{ value: "super_admin", label: "Super administrator" }, { value: "editor", label: "Editor curs" },
       { value: "examinator", label: "Examinator" }, { value: "support", label: "Suport" }];

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Editează ${mode === "student" ? "student" : "administrator"}` : `Adaugă ${mode === "student" ? "student" : "administrator"}`}>
      <form onSubmit={submit}>
        <div className="form-group"><label>Nume complet</label><input className="form-control" required value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
        <div className="form-group">
          <label>Email</label>
          <input className="form-control" type="email" required value={email} disabled={isEdit} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{isEdit ? "Parolă nouă (opțional)" : "Parolă inițială"}</label>
          <input className="form-control" type="text" required={!isEdit} value={password} onChange={(e) => setPassword(e.target.value)} />
          <small className="muted">{isEdit ? "Lasă gol pentru a păstra parola curentă." : "Va fi folosită la prima autentificare."}</small>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label>Rol</label>
            <select className="form-control" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select className="form-control" value={String(isActive)} onChange={(e) => setIsActive(e.target.value === "true")}>
              <option value="true">Activ</option>
              <option value="false">Suspendat</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Specialitate</label>
          <input className="form-control" value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
        </div>
        <div className="flex flex-between mt-24">
          {isEdit && onDelete ? (
            <button type="button" className="btn btn-accent btn-sm" onClick={onDelete}>
              <i className="fa-solid fa-trash"></i> Șterge
            </button>
          ) : <span />}
          <div className="flex gap-8" style={{ marginLeft: "auto" }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Anulează</button>
            <button type="submit" className="btn btn-primary"><i className="fa-solid fa-floppy-disk"></i> Salvează</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
